import { API_BASE_URL } from "../config";

const apiUrl = `${API_BASE_URL}/api`;

/**
 * Calls the backend API to list files in a given SharePoint folder.
 * @param folderUrl The URL of the SharePoint folder.
 * @param accessToken The user's Microsoft Entra ID access token.
 * @returns The list of files from the backend.
 */
export const listFiles = async (folderUrl: string, accessToken: string): Promise<any> => {
  // We construct the full URL for the API endpoint in the dedicated 'files' module.
  const url = `${apiUrl}/files/list-files`;

  // We prepare the options for the fetch call, including the method, headers, and body.
  const options = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ folder_url: folderUrl }),
  };
  // Security: do not log request URLs/bodies/identifiers in production.

  try {
    const response = await fetch(url, options);

    if (!response.ok) {
      // If the server returns an error, we try to parse the JSON body for a detailed message.
      const errorData = await response
        .json()
        .catch(() => ({ detail: "Failed to list files and could not parse error response." }));
      throw new Error(errorData.detail);
    }

    // If the request was successful, we parse and return the JSON response.
    return response.json();
  } catch (error) {
    // This catch block handles network errors (e.g., the backend server is down) or CORS issues.
    console.error("A network error occurred during the listFiles API call:", error);
    throw new Error(
      `Network request failed. Please ensure the backend server is running and accessible.`
    );
  }
};

export const getFileContent = async (fileUrl: string, accessToken: string): Promise<any> => {
  const url = `${apiUrl}/files/get-content`;

  const options = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ file_url: fileUrl }),
  };

  try {
    const response = await fetch(url, options);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        detail: "Failed to get file content and could not parse error response.",
      }));
      throw new Error(errorData.detail);
    }

    return response.json();
  } catch (error) {
    console.error("A network error occurred during the getFileContent API call:", error);
    throw new Error(
      `Network request failed. Please ensure the backend server is running and accessible.`
    );
  }
};

export const downloadFileBase64 = async (fileUrl: string, accessToken: string): Promise<string> => {
  const url = `${apiUrl}/files/download-base64`;

  const options = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ file_url: fileUrl }),
  };

  try {
    const response = await fetch(url, options);
    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: "Failed to download file and could not parse error response." }));
      throw new Error(errorData.detail);
    }
    const data = await response.json();
    return data.base64 as string;
  } catch (error) {
    console.error("A network error occurred during the downloadFileBase64 API call:", error);
    throw new Error(
      "Network request failed. Please ensure the backend server is running and accessible."
    );
  }
};

export const downloadFileBase64ByIds = async (
  driveId: string,
  itemId: string,
  accessToken: string
): Promise<string> => {
  const url = `${apiUrl}/files/download-base64`;

  const options = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ drive_id: driveId, item_id: itemId }),
  };

  try {
    const response = await fetch(url, options);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        detail: "Failed to download file (by IDs) and could not parse error response.",
      }));
      throw new Error(errorData.detail);
    }
    const data = await response.json();
    return data.base64 as string;
  } catch (error) {
    console.error("A network error occurred during the downloadFileBase64ByIds API call:", error);
    throw new Error(
      "Network request failed. Please ensure the backend server is running and accessible."
    );
  }
};

export interface FolderBrowseQuery {
  parent_drive_id?: string;
  parent_item_id?: string;
  top?: number;
  skip_token?: string;
}

export interface FolderItem {
  id: string;
  name: string;
  driveId: string;
  webUrl: string;
  isFolder: boolean;
}

export interface FolderBrowseResult {
  items: FolderItem[];
  nextSkipToken?: string | null;
}

export interface AppFolderInfo {
  created: boolean;
  path?: string | null;
  webUrl?: string | null;
  driveId?: string | null;
  itemId?: string | null;
  name?: string | null;
}

export const ensureAppFolder = async (accessToken: string): Promise<AppFolderInfo> => {
  // POST keeps the contract consistent with other file endpoints that mutate state.
  const url = `${apiUrl}/files/setup/app-folder`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ detail: "Unable to set up the Sourcestone folder." }));
    throw new Error(errorData.detail || "Unable to set up the Sourcestone folder.");
  }

  return response.json();
};

export const getAppFolder = async (accessToken: string): Promise<AppFolderInfo> => {
  const url = `${apiUrl}/files/app-folder`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ detail: "Unable to locate the Sourcestone folder." }));
    throw new Error(errorData.detail || "Unable to locate the Sourcestone folder.");
  }

  return response.json();
};

export const listFolders = async (
  query: FolderBrowseQuery,
  accessToken: string
): Promise<FolderBrowseResult> => {
  const url = `${apiUrl}/files/folders`;
  const options = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(query || {}),
  };

  const resp = await fetch(url, options);
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ detail: "Failed to list folders" }));
    throw new Error(err.detail || "Failed to list folders");
  }
  return resp.json();
};

export const listFilesByIds = async (
  driveId: string,
  itemId: string,
  accessToken: string
): Promise<any> => {
  const url = `${apiUrl}/files/list-files`;
  const options = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ drive_id: driveId, item_id: itemId }),
  };
  const response = await fetch(url, options);
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: "Failed to list files" }));
    throw new Error(err.detail || "Failed to list files");
  }
  return response.json();
};
